'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import ContactModal from './ContactModal';

interface FAQ {
    question: string;
    answer: string;
    category: 'method' | 'privacy' | 'account' | 'pricing';
}

const FAQSection = () => {
    const [collapsedFaqs, setCollapsedFaqs] = useState<number[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isContactOpen, setIsContactOpen] = useState(false);

    const faqs: FAQ[] = [
        {
            question: 'What makes YourAura different from generic horoscope apps?',
            answer: 'YourAura is a personal spiritual dashboard, not a single horoscope feed. It combines Daily Aura, horoscope, Vedic charts, numerology, palm reading, tarot, dreams, Full Pattern, compatibility, Planet Impact, Guidance, Day Decoder, saved readings, favorites, and community tools in one guided space.',
            category: 'method',
        },
        {
            question: 'How is my personal data protected?',
            answer: 'We use service providers to run YourAura: Google Gemini processes inputs included in AI-assisted readings, Google Analytics measures site usage, and Razorpay processes payments. We do not sell personal information. Account deletion removes product records, while billing records and a deletion record are retained. See our privacy policy for details.',
            category: 'privacy',
        },
        {
            question: 'Can I create an account now?',
            answer: 'Yes. YourAura is open to new users now. Create an account, verify your email, and open your private dashboard.',
            category: 'account',
        },
        {
            question: 'What plans are available?',
            answer: 'You can start with a free account. Paid plans add deeper reports, history, favorites, and relationship or community features when enabled.',
            category: 'pricing',
        },
        {
            question: 'How does AI improve traditional astrology?',
            answer: 'AI helps synthesize chart placements, numerology cycles, palm observations, tarot draws, dream themes, compatibility context, and Planet Impact signals into clear language. Traditional calculations provide the structure; AI helps translate them into readable guidance.',
            category: 'method',
        },
        {
            question: 'Can I use YourAura without uploading palm photos?',
            answer: 'Yes! Palm reading is optional. You\'ll still receive personalized daily insights based on your birth chart and numerology. Palm reading adds an additional layer of analysis but isn\'t required. You can add it later if you change your mind.',
            category: 'privacy',
        },
        {
            question: 'How long does it take to get my first reading?',
            answer: 'Your first preview arrives right after signup and profile setup. From there, the dashboard opens into daily aura, horoscope, charts, numerology, palm reading, tarot, dreams, guidance, Day Decoder, history, favorites, and community features.',
            category: 'account',
        },
        {
            question: 'Is there a money-back guarantee?',
            answer: 'Free account access does not require a refund. Paid plan terms are shown before checkout when you choose to upgrade.',
            category: 'pricing',
        },
    ];

    const filteredFaqs = faqs.filter((faq) =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleFaq = (index: number) => {
        setCollapsedFaqs((current) =>
            current.includes(index)
                ? current.filter((item) => item !== index)
                : [...current, index],
        );
    };

    return (
        <section id="faq" className="relative overflow-hidden border-y border-[var(--experience-border)] bg-[var(--experience-page-elevated)] py-24">

            <ContactModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />

            <div className="container mx-auto px-5 relative z-10">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16 space-y-4">
                        <h2 className="text-4xl md:text-5xl font-bold text-foreground font-playfair tracking-wide animate-in fade-in slide-in-from-bottom-8 duration-700">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-xl text-muted-foreground font-light tracking-wide animate-in fade-in slide-in-from-bottom-8 duration-700 delay-100">
                            Direct answers about method, privacy, account access, and pricing.
                        </p>
                    </div>

                    <div className="mb-10 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-200">
                        <div className="relative group">
                            <Icon
                                name="MagnifyingGlassIcon"
                                size={20}
                                className="absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-accent transition-colors duration-300"
                            />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search questions..."
                                className="w-full pl-14 pr-6 py-4 manuscript-input rounded-xl text-foreground placeholder:text-[#ead8b7]/38 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent/50 transition-all duration-300 backdrop-blur-sm"
                            />
                        </div>
                    </div>

                    <div className="space-y-4">
                        {filteredFaqs.map((faq, index) => {
                            const isCollapsed = collapsedFaqs.includes(index);

                            return (
                            <div
                                key={index}
                                className={`card-surface manuscript-frame rounded-xl overflow-hidden transition-colors duration-200 ${!isCollapsed ? 'border-accent/30' : ''}`}
                            >
                                <button
                                    onClick={() => toggleFaq(index)}
                                    className="w-full px-6 py-5 flex items-center justify-between text-left group"
                                    aria-expanded={!isCollapsed}
                                >
                                    <span className="font-semibold text-foreground pr-4 text-lg group-hover:text-accent transition-colors">{faq.question}</span>
                                    <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200 ${!isCollapsed ? 'bg-accent text-[#1d1509] rotate-180' : 'bg-[var(--experience-surface-raised)] text-muted-foreground'}`}>
                                        <Icon
                                            name="ChevronDownIcon"
                                            size={20}
                                            className="transition-transform"
                                        />
                                    </div>
                                </button>

                                {!isCollapsed && (
                                    <div className="px-6 pb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div className="h-px w-full bg-[#e8d7b0]/10 mb-4"></div>
                                        <p className="text-muted-foreground leading-relaxed text-base">{faq.answer}</p>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>

                    {filteredFaqs.length === 0 && (
                        <div className="text-center py-16 animate-in fade-in zoom-in duration-300">
                            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#e8d7b0]/10">
                                <Icon name="QuestionMarkCircleIcon" size={40} className="text-[#ead8b7]/44" />
                            </div>
                            <h3 className="text-xl font-bold text-foreground mb-2">No results found</h3>
                            <p className="text-[#ead8b7]/56">Try adjusting your search terms</p>
                        </div>
                    )}

                    <div className="mt-16 card-surface manuscript-frame rounded-2xl p-10 text-center relative overflow-hidden animate-in fade-in slide-in-from-bottom-12 duration-700 delay-300">
                        <Icon name="ChatBubbleLeftRightIcon" size={40} className="text-accent mx-auto mb-6" />
                        <h3 className="text-2xl font-bold text-foreground font-playfair tracking-wide mb-3 relative z-10">
                            Still Have Questions?
                        </h3>
                        <p className="text-muted-foreground mb-8 max-w-lg mx-auto relative z-10">
                            We can help you decide whether the daily-brief format is a fit before you create an account.
                        </p>
                        <button
                            onClick={() => setIsContactOpen(true)}
                            className="manuscript-button relative z-10 inline-block rounded-xl px-8 py-4 font-bold"
                        >
                            Contact Support
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;
